import apiClient from '@/services/api'

export type ModulePermission = 'germplasm' | 'breeding-plan' | 'field-stations' | 'task-management'

export interface AuthUser {
  id: string
  username: string
  phone?: string
  full_name: string
  real_name?: string
  company?: string
  company_id?: string
  branch_company?: string
  branch_company_id?: string
  department?: string
  department_id?: string
  position_id?: string
  position_name?: string
  manager_name?: string
  direct_manager_user_id?: string
  is_active?: boolean
  must_change_password?: boolean
  organization_level?: 'company' | 'branch' | 'department' | 'group'
  organization_path?: string[]
  is_organization_leader?: boolean
  is_admin: boolean
  module_permissions: ModulePermission[]
  project_permissions: string[]
  project_permission_grants: Array<{
    project_id: string
    decision_role: 'viewer' | 'proposer' | 'approver'
  }>
  station_permissions: string[]
  must_change_password?: boolean
}

export interface LoginResponse {
  token: string
  expires_at: string
  user: AuthUser
}

export interface RegisterPayload {
  username: string
  password: string
  full_name: string
  department?: string
  position_id?: string
  manager_name?: string
  portal?: 'user'
}

export interface RegisterResponse {
  message: string
  user: AuthUser
}

export interface AdminUserPayload {
  username: string
  password: string
  full_name: string
  real_name?: string
  phone?: string
  company?: string
  company_id?: string
  branch_company?: string
  branch_company_id?: string
  department?: string
  department_id?: string
  position_id?: string
  manager_name?: string
  direct_manager_user_id?: string
  is_admin: boolean
  module_permissions: ModulePermission[]
  project_permissions: string[]
  project_permission_grants?: Array<{
    project_id: string
    decision_role: 'viewer' | 'proposer' | 'approver'
  }>
  station_permissions: string[]
}

export interface AdminUserUpdatePayload {
  password?: string
  full_name?: string
  real_name?: string
  phone?: string
  company?: string
  company_id?: string
  branch_company?: string
  branch_company_id?: string
  department?: string
  department_id?: string
  position_id?: string
  direct_manager_user_id?: string
  manager_name?: string
  is_admin?: boolean
  is_active?: boolean
  module_permissions?: ModulePermission[]
  project_permissions?: string[]
  project_permission_grants?: Array<{
    project_id: string
    decision_role: 'viewer' | 'proposer' | 'approver'
  }>
  station_permissions?: string[]
  must_change_password?: boolean
}

export const authApi = {
  login: async (username: string, password: string, portal: 'admin' | 'user') => {
    return apiClient.post<LoginResponse>('/auth/login', { username, password, portal })
  },

  register: async (payload: RegisterPayload) => {
    return apiClient.post<RegisterResponse>('/auth/self-register', { ...payload, portal: 'user' })
  },

  me: async () => {
    return apiClient.get<AuthUser>('/auth/me')
  },

  updateProfile: async (payload: { full_name: string; department?: string; manager_name?: string }) => {
    return apiClient.put<AuthUser>('/auth/profile', payload)
  },

  listUsers: async () => {
    // 后端回的是 items，不是 data。写成 data 的时候类型是断言出来的、编译不报错，
    // 结果「派活给谁」的下拉永远是空的 —— 管理端根本派不出活。实测才发现。
    return apiClient.get<{ total: number; items: AuthUser[] }>('/admin/users')
  },

  createUser: async (payload: AdminUserPayload) => {
    return apiClient.post<AuthUser>('/admin/users', payload)
  },

  updateUser: async (userId: string, payload: AdminUserUpdatePayload) => {
    return apiClient.put<AuthUser>(`/admin/users/${userId}`, payload)
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    return apiClient.put<{ message: string }>('/auth/password', { current_password: currentPassword, new_password: newPassword })
  },
}
